import { Storage } from '../utils.jsx'
import { getPlayerId } from './communityStats.js'

const SETTINGS_KEY = 'dailyReminderSettings'
const defaultSettings = { enabled: false, reminderTime: '19:00' }

export function remindersAreSupported() {
  return (
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  )
}

export function getStoredReminderSettings() {
  return Storage.getFromLocalStorage(SETTINGS_KEY, defaultSettings)
}

function saveSettings(settings) {
  Storage.saveToLocalStorage(SETTINGS_KEY, settings)
  return settings
}

function urlBase64ToUint8Array(value) {
  const padding = '='.repeat((4 - (value.length % 4)) % 4)
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  return Uint8Array.from(rawData, (character) => character.charCodeAt(0))
}

export async function getDailyReminderStatus() {
  const settings = getStoredReminderSettings()
  if (!remindersAreSupported() || Notification.permission !== 'granted') {
    return { ...settings, enabled: false }
  }

  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  return { ...settings, enabled: settings.enabled && Boolean(subscription) }
}

export async function enableDailyReminder(reminderTime) {
  if (!remindersAreSupported()) {
    throw new Error('Notifications are not supported in this browser.')
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.')
  }

  const registration = await navigator.serviceWorker.ready
  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    const keyResponse = await fetch('/api/notifications/public-key')
    if (!keyResponse.ok) throw new Error('Could not load notification settings.')
    const { publicKey } = await keyResponse.json()
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    })
  }

  const response = await fetch('/api/notifications/subscription', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      playerId: getPlayerId(),
      reminderTime,
      subscription: subscription.toJSON(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  })
  if (!response.ok) throw new Error('Could not save the daily reminder.')

  return saveSettings({ enabled: true, reminderTime })
}

export async function disableDailyReminder() {
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  await fetch('/api/notifications/subscription', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ playerId: getPlayerId() }),
  })
  await subscription?.unsubscribe()
  return saveSettings({
    ...getStoredReminderSettings(),
    enabled: false,
  })
}

export async function sendTestReminder() {
  if (!remindersAreSupported() || Notification.permission !== 'granted') {
    throw new Error('Notification permission was not granted.')
  }

  const registration = await navigator.serviceWorker.ready
  await registration.showNotification('Chaindle reminders are on', {
    body: `We’ll remind you every day at ${getStoredReminderSettings().reminderTime}.`,
    icon: '/icon/maskable_icon_196x196.png',
    badge: '/icon/lowres.png',
    tag: 'chaindle-daily-reminder-test',
    data: { url: '/' },
  })
}
